import React from 'react';

import {useScientificModelRegistry} from 'model-hub/scientific-models/useAvailableScientificModels';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import ChipPopoverContent from 'ui-library/chips/ChipPopoverContent';
import {getSharedSmartChipProps, SharedSmartChipProps} from 'ui-library/chips/ChipUtil';
import SmartChip from 'ui-library/chips/SmartChip';
import useSmartChipQuery, {ChipItem, SmartChipType} from 'ui-library/chips/useSmartChipQuery';
import Spinner from 'ui-library/Spinner';
import {readableTime} from 'util/DateUtil';

export interface PredictionBatchChipProps extends SharedSmartChipProps<SmartChipType.PREDICTION_BATCH> {}

function PredictionBatchModelDisplayName({modelName}: {modelName: string}): JSX.Element {
  const scientificModelRegistry = useScientificModelRegistry();

  if (scientificModelRegistry.type !== 'ready') {
    return <Spinner style={{height: 12, margin: 0, verticalAlign: 'middle', width: 12}} />;
  }

  return (
    <>
      {scientificModelRegistry.registry.catalogByExternalReference.get(modelName)?.displayName ?? modelName}
    </>
  );
}

function PredictionBatchChipPopoverContent({
  predictionBatch,
}: {
  predictionBatch: ChipItem<SmartChipType.PREDICTION_BATCH>;
}): JSX.Element {
  return (
    <ChipPopoverContent
      info={{
        fields: [
          {label: 'Name', value: predictionBatch.name},
          {label: 'Type', value: 'Prediction batch'},
          {
            label: 'Creator',
            value: (
              <>
                <Avatar entity={predictionBatch.creator} size={AvatarSize.XSMALL} />
                &nbsp;{predictionBatch.creator.name}
              </>
            ),
          },
          {label: 'Created', value: readableTime(predictionBatch.createdAt)},
          {
            label: 'Model',
            value: <PredictionBatchModelDisplayName modelName={predictionBatch.modelName} />,
          },
        ],
      }}
    />
  );
}

export default function PredictionBatchChip(props: PredictionBatchChipProps): JSX.Element {
  const sharedSmartChipProps = getSharedSmartChipProps(props);
  const {id, onClick} = sharedSmartChipProps;

  const {
    item: predictionBatch,
    handleClickLink,
    isInaccessible,
    itemUrl,
  } = useSmartChipQuery<SmartChipType.PREDICTION_BATCH>({
    id,
    onClick,
    getUrlFn: ({editUrl}) => editUrl,
  });

  return (
    <SmartChip
      {...sharedSmartChipProps}
      icon={null}
      isInaccessible={isInaccessible}
      item={predictionBatch}
      onClick={handleClickLink}
      popoverContentOverride={
        predictionBatch != null ? (
          <PredictionBatchChipPopoverContent predictionBatch={predictionBatch} />
        ) : null
      }
      url={itemUrl}
    />
  );
}
